<div class="agents-container">
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Agents</h1>
        <p>Manage and view all agent information</p>
      </div>
      <button class="create-agent-btn" (click)="openCreateModal()">
        <i class="fas fa-plus"></i> Create Agent
      </button>
    </div>
  </div>

  <!-- Filters -->
  <div class="filters-container">
    <div class="filter-group">
      <label for="zoneFilter">Filter by Zone:</label>
      <select id="zoneFilter" [(ngModel)]="selectedZoneFilter" (ngModelChange)="onFilterChange()" class="filter-select">
        <option value="">All Zones</option>
        <option *ngFor="let zone of zones" [value]="zone.id">{{ zone.name }}</option>
      </select>
    </div>

    <div class="filter-group">
      <label for="statusFilter">Filter by Status:</label>
      <select id="statusFilter" [(ngModel)]="statusFilter" (ngModelChange)="onFilterChange()" class="filter-select">
        <option value="">All Statuses</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
    </div>
  </div>

  <div class="table-container" *ngIf="(agents$ | async) as agentsResponse">
    <!-- Pagination Summary -->
    <div class="pagination-summary">
      <p>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount || 0) }} of {{
        totalCount || 0 }} agents</p>
    </div>

    <table class="agents-table">
      <thead>
        <tr>
          <th>Agent ID</th>
          <th>Name</th>
          <th>Phone Number</th>
          <th>Status</th>
          <th>Zones</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let agent of agentsResponse?.items || []">
          <!-- Main row -->
          <tr class="agent-row" [class.expanded]="isRowExpanded(agent.agentId)">
            <td>{{ agent.agentId }}</td>
            <td>
              <div class="flex items-center gap-2">
                <app-agent-avatar [url]="pictures.urls[agent.agentId]" [name]="agent.name"></app-agent-avatar>
                <span>{{ agent.name }}</span>
              </div>
            </td>
            <td>{{ agent.phoneNumber }}</td>
            <td>
              <span [class]="getStatusBadgeClass(agent.isActive)">
                {{ getStatusText(agent.isActive) }}
              </span>
            </td>
            <td>
              <span class="zones-summary">
                {{ getZoneNames(agent.zones) }}
                <span *ngIf="agent.zones && agent.zones.length > 0" class="zone-count">
                  ({{ agent.zones.length }})
                </span>
              </span>
            </td>
            <td>
              <div class="action-buttons">
                <button class="expand-btn" (click)="toggleRow(agent.agentId)"
                  [class.expanded]="isRowExpanded(agent.agentId)" title="Expand to see details">
                  {{ isRowExpanded(agent.agentId) ? '▼' : '▶' }}
                </button>

                <button class="assign-zone-btn" (click)="openAssignZoneModal(agent)" title="Assign Zone">
                  <i class="fas fa-map-marker-alt"></i>
                </button>

                <button class="assign-pricing-group-btn" (click)="openAssignPricingGroupModal(agent)" title="Assign Pricing Group">
                  <i class="fas fa-tags"></i>
                </button>

                <button [class]="getToggleButtonClass(agent.isActive)" (click)="toggleAgentStatus(agent)"
                  [disabled]="isAgentToggling(agent.agentId)" [title]="getToggleButtonText(agent.isActive) + ' Agent'">
                  <i class="fas" [class.fa-toggle-on]="agent.isActive" [class.fa-toggle-off]="!agent.isActive"
                    *ngIf="!isAgentToggling(agent.agentId)"></i>
                  <i class="fas fa-spinner fa-spin" *ngIf="isAgentToggling(agent.agentId)"></i>
                  {{ getToggleButtonText(agent.isActive) }}
                </button>
              </div>
            </td>
          </tr>

          <!-- Expanded row with zone details -->
          <tr class="details-row" *ngIf="isRowExpanded(agent.agentId)">
            <td colspan="6">
              <div class="details-container">
                <div class="details-section">
                  <h4>Profile Photo</h4>
                  <app-agent-profile-picture-editor [agentId]="agent.agentId" [agentName]="agent.name"
                    [url]="pictures.urls[agent.agentId] || null"></app-agent-profile-picture-editor>
                </div>
                <div class="details-section">
                  <h4>Agent Details</h4>
                  <div class="details-grid">
                    <div class="detail-item">
                      <span class="detail-label">Full Name:</span>
                      <span class="detail-value">{{ agent.name }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Phone Number:</span>
                      <span class="detail-value">{{ agent.phoneNumber }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Status:</span>
                      <span [class]="getStatusBadgeClass(agent.isActive)">
                        {{ getStatusText(agent.isActive) }}
                      </span>
                    </div>
                     <div class="detail-item">
                      <span class="detail-label">Internal ID:</span>
                      <span class="detail-value">{{ agent.id }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Pricing Group:</span>
                      <span class="detail-value" style="display: flex; align-items: center; gap: 0.5rem;">
                        {{ getPricingGroupName(agent.pricingGroupId) }}
                        <button *ngIf="agent.pricingGroupId" class="text-xs text-red-500 hover:text-red-700 ml-2" (click)="removePricingGroup(agent, $event)" title="Remove Pricing Group" style="background: none; border: none; color: #e74c3c; cursor: pointer; display: inline-flex; align-items: center; gap: 0.25rem;">
                          <i class="fas fa-unlink"></i> Remove
                        </button>
                      </span>
                    </div>
                  </div>
                  <div class="details-actions mt-4 pt-3 border-t border-gray-100 flex gap-2">
                    <a [routerLink]="['/agents/attendance', agent.agentId]" class="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors inline-flex items-center gap-1">
                      <i class="fas fa-calendar-alt"></i> Attendance Calendar
                    </a>
                  </div>
                </div>

                <div class="details-section" *ngIf="agent.zones && agent.zones.length > 0">
                  <h4>Assigned Zones ({{ agent.zones.length }})</h4>
                  <div class="zones-grid">
                    <div *ngFor="let zoneId of agent.zones" class="zone-card"
                      [class.confirming]="isZoneRemoving(agent.agentId, zoneId)">
                      <div class="zone-content-wrapper" *ngIf="!isZoneRemoving(agent.agentId, zoneId)">
                        <div class="zone-name">{{ getZoneName(zoneId) }}</div>
                        <div class="zone-id">ID: {{ zoneId }}</div>
                        <button class="remove-zone-mini-btn" (click)="startZoneRemoval(agent.agentId, zoneId, $event)"
                          title="Remove Zone">
                          <i class="fas fa-trash-alt"></i>
                        </button>
                      </div>

                      <div class="zone-confirm-wrapper" *ngIf="isZoneRemoving(agent.agentId, zoneId)">
                        <div class="confirm-text">Confirm Removal?</div>
                        <div class="confirm-actions">
                          <button class="confirm-btn" (click)="confirmZoneRemoval(agent, zoneId, $event)"
                            title="Confirm">
                            <i class="fas fa-check"></i>
                          </button>
                          <button class="cancel-btn" (click)="cancelZoneRemoval($event)" title="Cancel">
                            <i class="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="details-section" *ngIf="!agent.zones || agent.zones.length === 0">
                  <div class="no-zones">
                    <i class="fas fa-exclamation-triangle"></i>
                    <p>No zones assigned to this agent</p>
                    <button class="assign-zone-btn primary" (click)="openAssignZoneModal(agent)">
                      <i class="fas fa-map-marker-alt"></i> Assign Zone
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </div>

  <div class="loading" *ngIf="isLoading">
    <div class="loading-spinner">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Loading agents...</p>
    </div>
  </div>

  <div class="error" *ngIf="(agents$ | async) as agentsResponse">
    <div *ngIf="!isLoading && (!agentsResponse.items || agentsResponse.items.length === 0)" class="no-data">
      <i class="fas fa-users"></i>
      <p>No agents found.</p>
      <button class="create-agent-btn" (click)="openCreateModal()">
        <i class="fas fa-plus"></i> Create First Agent
      </button>
    </div>
  </div>

  <!-- Pagination Component -->
  <app-pagination *ngIf="(agents$ | async) as agentsResponse" [page]="currentPage" [totalPages]="totalPages"
    [pageSize]="pageSize" (pageChange)="onPageChange($event)" (pageSizeChange)="onPageSizeChange($event)">
  </app-pagination>

  <!-- Create Agent Modal -->
  <app-create-agent *ngIf="showCreateModal" (agentCreated)="onAgentCreated()" (closeModal)="closeCreateModal()">
  </app-create-agent>

  <!-- Assign Zone Modal -->
  <app-assign-agent-zone *ngIf="showAssignZoneModal && selectedAgent" [agent]="selectedAgent" [zones]="zones"
    (zoneAssigned)="onZoneAssigned()" (closeModal)="closeAssignZoneModal()">
  </app-assign-agent-zone>

  <!-- Assign Pricing Group Modal -->
  <app-assign-agent-pricing-group *ngIf="showAssignPricingGroupModal && selectedAgent" [agent]="selectedAgent"
    (pricingGroupAssigned)="onPricingGroupAssigned()" (closeModal)="closeAssignPricingGroupModal()">
  </app-assign-agent-pricing-group>

  <!-- Notification Toast -->
  <div class="notification-toast" [class]="notification.type" [class.show]="notification.show">
    <div class="notification-content">
      <i class="fas" [class.fa-check-circle]="notification.type === 'success'"
        [class.fa-exclamation-circle]="notification.type === 'error'"
        [class.fa-info-circle]="notification.type === 'info'"></i>
      <span class="notification-message">{{ notification.message }}</span>
    </div>
    <button class="notification-close" (click)="closeNotification()">
      <i class="fas fa-times"></i>
    </button>
  </div>
</div>
